// D1 — Endpoint drill-down page (/workspace/:apiId/endpoints/:endpointId)
//
// 这页跟 analytics-traffic 的整体结构一模一样，只是所有 endpoint-scoped
// API 调用都传入 endpointId 把视图缩到单个 endpoint。复用 80% 现有组件：
//   - WorkspaceShell 包含左侧 nav（active=apis 段）让用户能回到管理 API
//   - 顶部 breadcrumb: ← [API 名] · GET /v2/search
//   - KPI Grid: calls / success / unique callers / latency / p50 / p95
//   - 调用量时序 + 延迟时序 (mean only, P50/P95 在 endpoint 维度没预聚合)
//   - 成功/失败堆叠
//   - 延迟构成
//   - Top callers (本 endpoint)
//   - 错误分组 (本 endpoint)
//   - 调用日志 (auto-filtered to this endpoint)

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { XShell } from '../shell.jsx';
import { exactComparisonBounds } from './workspace-sections/analytics-window.js';
import { useDetailResource, useDetailPanel, useDetailLogs, historicalDetailError } from './workspace-sections/detail-resources.js';
import { XApi } from '../services.js';
import { WorkspaceShell } from '../ui/workspace-shell.jsx';
import {
  KpiCard,
  KpiGrid,
  RangePicker,
  Section,
  ChartGrid,
  BarChart,
  LineChart,
  StackedBarChart,
  HorizontalStack,
  EmptyBarChart,
  EmptyTable,
  LoadingHint,
  StatusCodePill,
  MethodPill,
  CountryCell,
  DataTable,
  TableFooter,
  ExportCsvButton,
  fmtNum,
  fmtPct,
  fmtUsd,
  fmtTinyUsd,
} from './workspace-sections/analytics-shared.jsx';


// ms -> compact human duration (async turnaround can be minutes)
const fmtMs = (ms) =>
  ms == null
    ? '—'
    : ms >= 60000
    ? `${(ms / 60000).toFixed(1)}m`
    : ms >= 1000
    ? `${(ms / 1000).toFixed(1)}s`
    : `${ms}ms`;

// bytes -> compact human size
const fmtBytes = (n) => {
  if (n == null) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)} KB`;
  return `${n} B`;
};

export const WorkspaceEndpointPage = () => {
  const { authed, user } = XShell.useAuth();
  const { route } = XShell.useRoute();
  const owner = user?._raw?.id ?? user?.id ?? null;
  return authed ? <EndpointState key={JSON.stringify([owner, route.params.apiId, route.params.endpointId])} /> : <div>请登录后查看端点详情</div>;
};
const EndpointState = () => {
  const { route, navigate } = XShell.useRoute();
  const apiId = route.params.apiId;
  const endpointId = route.params.endpointId;

  const [range, setRange] = useState('30d');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [preferredGranularity, setGranularity] = useState('day');
  const bounds = useMemo(() => exactComparisonBounds(range, customRange), [range, customRange.from, customRange.to]);
  const windowKey = JSON.stringify([apiId, endpointId, bounds]);
  const granularity = bounds && +new Date(bounds.to)-+new Date(bounds.from)+1<=36*3600000 ? preferredGranularity : 'day';
  const loadApi = useCallback(() => XApi.apiSvc.get(apiId), [apiId]);
  const metadata = useDetailResource(apiId, loadApi);
  const api = metadata.data;
  const dashboard = XApi.providerDashboardService;
  const mainResource = useDetailPanel(windowKey, Boolean(bounds), dashboard.endpointDetail, apiId, bounds, endpointId);
  const endpointDetail = mainResource.data ?? {endpoint:{id:endpointId,name:'Endpoint',method:'',path:''},capabilities:{},totalCalls:0,prevTotalCalls:0,prevSuccessCalls:0,prevGrossEarnings:0};
  const ready = Boolean(bounds && mainResource.data);
  const options = useMemo(() => ({ ...bounds, endpointId }), [bounds, endpointId]);
  const timeseriesOptions = useMemo(() => ({ ...options, granularity }), [options, granularity]);
  const revenueOptions = useMemo(() => ({ ...options, limit:25 }), [options]);
  const tsResource = useDetailPanel(windowKey+granularity, ready, dashboard.apiTimeseries, apiId, timeseriesOptions);
  const latencyResource = useDetailPanel(windowKey, ready, dashboard.apiLatencyBreakdown, apiId, options);
  const errorsResource = useDetailPanel(windowKey, ready, dashboard.apiErrorGroups, apiId, options);
  const revenueResource = useDetailPanel(windowKey, ready, dashboard.apiCallersRevenue, apiId, revenueOptions);
  const taskResource = useDetailPanel(windowKey, ready && (endpointDetail.capabilities?.asyncMode ?? 'NONE') !== 'NONE', dashboard.endpointTaskStats, apiId, bounds, endpointId);
  const aiResource = useDetailPanel(windowKey, ready && endpointDetail.capabilities?.isAi, dashboard.endpointAiUsage, apiId, bounds, endpointId);
  const wsResource = useDetailPanel(windowKey, ready && endpointDetail.capabilities?.isWebSocket, dashboard.endpointWsSessions, apiId, bounds, endpointId);
  const timeseries = tsResource.data, latencyBreakdown = latencyResource.data, errorGroups = errorsResource.data, callerRevenue = revenueResource.data;
  const taskStats = taskResource.data, aiUsage = aiResource.data, wsSessions = wsResource.data;
  const panelsLoading = Boolean(mainResource.loading);
  const filter = useMemo(() => ({ endpointId }), [endpointId]);
  const { logs, logCursor, logHasMore, logsLoading, logsError, fetchLogs } = useDetailLogs(apiId, bounds, filter);
  const [selectedLog, setSelectedLog] = useState(null);
  const activeLog = selectedLog?.key === windowKey ? selectedLog.value : null;
  const setActiveLog = value => setSelectedLog({key:windowKey,value});
  const onSelect = (key, sub) => {
    // 切到其他段时回到 workspace-api 路由
    navigate(
      'workspace-api',
      sub ? { apiId, section: key, subsection: sub } : { apiId, section: key },
    );
  };

  const backToApiManager = () => {
    navigate('workspace-api', { apiId, section: 'apis' });
  };

  if (!api) return <div>{metadata.error ? <><span role="alert">API 信息加载失败</span><button onClick={metadata.retry}>重试</button></> : 'Loading endpoint…'}</div>;
  const ep = endpointDetail.endpoint;
  const prevTotalCalls = endpointDetail.prevTotalCalls;
  const prevGross = endpointDetail.prevGrossEarnings;
  const successRatePrev =
    prevTotalCalls > 0 ? (endpointDetail.prevSuccessCalls / prevTotalCalls) * 100 : 0;

  return (
    <WorkspaceShell
      api={api}
      activeSection="apis"
      activeSubsection={null}
      onSelect={onSelect}
    >
      <div style={{ padding: '24px 32px 64px' }}>
        {/* breadcrumb */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
            fontSize: 11,
            color: 'var(--fg-3)',
          }}
        >
          <button
            type="button"
            onClick={backToApiManager}
            style={{
              padding: '2px 8px',
              background: 'transparent',
              border: '1px solid var(--bd-1)',
              borderRadius: 4,
              color: 'var(--fg-2)',
              cursor: 'pointer',
              fontSize: 11,
            }}
          >← {api.name}</button>
          <span>/</span>
          {ep.method && <MethodPill method={ep.method} />}
          <span style={{ color: 'var(--fg-0)' }}>{ep.name}</span>
          <span className="mono" style={{ color: 'var(--fg-3)' }}>{ep.path}</span>
        </div>

        {/* 顶部 header + RangePicker */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--fg-0)', letterSpacing: '-0.01em' }}>
              {ep.name}
            </h2>
            <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
              Endpoint drill-down · 全维度聚合到此 endpoint
            </div>
          </div>
          <RangePicker
            value={range}
            onChange={setRange}
            customRange={customRange}
            onCustomRangeChange={setCustomRange}
          />
        </div>

        {/* 空态横幅：当 endpoint 在窗口内 0 调用、且历史也是 0（prevTotal
            也是 0）时，整页除了 KPI 框就全是空 chart + "—"，看起来像"页面
            坏了"。明确告诉用户「这个 endpoint 根本没人调过」，并给出下一步
            可能的动作，避免 X API v2 那种 156 个 endpoint 里大半空着的
            情况让人困惑。 */}
        {!bounds && <div role="alert">日期范围必须完整、有效、正序且不超过 365 天（UTC）</div>}
        {mainResource.error && <div role="alert">{historicalDetailError(mainResource.error) ?? '端点数据加载失败'} <button onClick={mainResource.retry}>重试数据</button></div>}
        {mainResource.loading && <LoadingHint />}
        {[[tsResource,'时序'],[latencyResource,'延迟'],[errorsResource,'错误分组'],[revenueResource,'调用方收入'],[taskResource,'任务'],[aiResource,'AI'],[wsResource,'WS']].map(([r,label]) => r.error ? <div role="alert" key={label}>{historicalDetailError(r.error) ?? `${label}暂不可用`} <button onClick={r.retry}>重试{label}</button></div> : null)}
        {mainResource.data && <>
        {!panelsLoading && endpointDetail.totalCalls === 0 && prevTotalCalls === 0 && (
          <div
            style={{
              padding: '14px 16px',
              marginBottom: 16,
              background: 'var(--bg-1)',
              border: '1px solid var(--bd-1)',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              fontSize: 12,
              color: 'var(--fg-2)',
              lineHeight: 1.6,
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1, marginTop: 1 }}>📭</span>
            <div>
              <div style={{ color: 'var(--fg-0)', fontWeight: 500, marginBottom: 2 }}>
                所选窗口暂无 REST 调用
              </div>
              <div>
                所选窗口内没有 REST 调用，其他日期及任务 / AI / WS 面板请分别查看。想试调用可去
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('workspace-api', { apiId, section: 'tests' });
                  }}
                  style={{ color: 'var(--fg-0)', textDecoration: 'underline', margin: '0 2px' }}
                >
                  自动化测试
                </a>
                段；或返回 API
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('workspace-api', { apiId, section: 'analytics', subsection: 'traffic' });
                  }}
                  style={{ color: 'var(--fg-0)', textDecoration: 'underline', margin: '0 2px' }}
                >
                  Traffic
                </a>
                看哪几个 endpoint 实际有流量。
              </div>
            </div>
          </div>
        )}

        {/* KPI */}
        <KpiGrid>
          <KpiCard
            label="总调用"
            value={panelsLoading ? '…' : fmtNum(endpointDetail.totalCalls)}
            dim={!panelsLoading && endpointDetail.totalCalls === 0}
            trend={{ current: endpointDetail.totalCalls, previous: prevTotalCalls }}
          />
          <KpiCard
            label="成功率"
            value={
              panelsLoading
                ? '…'
                : endpointDetail.totalCalls > 0
                ? fmtPct(endpointDetail.successRate / 100)
                : '—'
            }
            dim={!panelsLoading && endpointDetail.totalCalls === 0}
            trend={
              endpointDetail.totalCalls > 0 && prevTotalCalls > 0
                ? { current: endpointDetail.successRate, previous: successRatePrev }
                : null
            }
          />
          {/* Audit fix #12: was "唯一调用方" which falsely implied the same
              rolling-30d distinct semantic as the API-level KPI. Endpoint
              source is _max over daily rows — peak single-day distinct.
              Label + field name now self-document that. */}
          <KpiCard
            label="峰值日独立调用方"
            value={panelsLoading ? '…' : fmtNum(endpointDetail.peakDailyCallers)}
            dim={!panelsLoading && endpointDetail.peakDailyCallers === 0}
            hint="窗口内单日 distinct caller 峰值（非滚动 30d 去重）"
          />
          <KpiCard
            label="均延迟"
            value={
              panelsLoading ? '…'
              : endpointDetail.totalCalls > 0
                ? `${endpointDetail.avgLatency} ms`
                : '—'
            }
            dim={!panelsLoading && endpointDetail.totalCalls === 0}
            hint="mean，全 range"
          />
          <KpiCard
            label="上游均值"
            value={
              panelsLoading ? '…'
              : endpointDetail.avgUpstreamLatencyMs != null
                ? `${endpointDetail.avgUpstreamLatencyMs} ms`
                : '—'
            }
            dim={!panelsLoading && endpointDetail.avgUpstreamLatencyMs == null}
            hint="per-sample"
          />
          <KpiCard
            label="毛收入"
            value={panelsLoading ? '…' : fmtTinyUsd(endpointDetail.grossEarnings)}
            dim={!panelsLoading && endpointDetail.grossEarnings === 0}
            trend={{ current: endpointDetail.grossEarnings, previous: prevGross }}
          />
        </KpiGrid>

        {/* 能力面板（按端点类型叠加）：异步端点 → 任务面板；AI 端点 → AI 面板。
            一个端点既异步又 AI 时两个都显示。门控用 endpointDetail.capabilities。 */}

        {/* 异步任务面板 —— 仅 asyncMode 明确为非 NONE 时渲染（capabilities 缺失
            时——比如 api 尚未重启加载新字段——默认 NONE，不显示空面板） */}
        {(endpointDetail.capabilities?.asyncMode ?? 'NONE') !== 'NONE' && (
          <Section
            title="异步任务"
            action={
              <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                {endpointDetail.capabilities?.taskStrategy === 'UPSTREAM_POLLING'
                  ? 'V2 上游轮询'
                  : 'V1 Worker'}{' '}
                · 来自 ProviderTaskStatsDaily
              </span>
            }
          >
            {taskResource.loading ? (
              <LoadingHint />
            ) : taskStats && taskStats.submitted > 0 ? (
              <>
                <KpiGrid>
                  <KpiCard label="提交任务" value={fmtNum(taskStats.submitted)} />
                  <KpiCard
                    label="任务成功率"
                    value={
                      taskStats.successRate != null
                        ? fmtPct(taskStats.successRate / 100)
                        : '—'
                    }
                    dim={taskStats.successRate == null}
                    hint="成功 / 终态(成功+失败+过期)"
                  />
                  <KpiCard
                    label="失败"
                    value={fmtNum(taskStats.failed)}
                    dim={taskStats.failed === 0}
                  />
                  <KpiCard
                    label="过期"
                    value={fmtNum(taskStats.expired)}
                    dim={taskStats.expired === 0}
                    hint="未在期限内完成"
                  />
                  <KpiCard
                    label="平均周转"
                    value={fmtMs(taskStats.avgTurnaroundMs)}
                    dim={taskStats.avgTurnaroundMs == null}
                    hint="提交 → 完成"
                  />
                  <KpiCard
                    label="平均排队"
                    value={fmtMs(taskStats.avgQueueMs)}
                    dim={taskStats.avgQueueMs == null}
                    hint="提交 → 开始处理"
                  />
                  {endpointDetail.capabilities?.taskStrategy ===
                    'UPSTREAM_POLLING' && (
                    <KpiCard
                      label="平均轮询次数"
                      value={
                        taskStats.avgPollAttempts != null
                          ? taskStats.avgPollAttempts
                          : '—'
                      }
                      dim={taskStats.avgPollAttempts == null}
                    />
                  )}
                </KpiGrid>

                {/* 状态漏斗 */}
                <div style={{ marginTop: 10 }}>
                  <HorizontalStack
                    height={22}
                    segments={[
                      { label: `成功 ${taskStats.succeeded}`, value: taskStats.succeeded, color: '#22a06b' },
                      { label: `失败 ${taskStats.failed}`, value: taskStats.failed, color: '#e5484d' },
                      { label: `过期 ${taskStats.expired}`, value: taskStats.expired, color: '#e08e0b' },
                      { label: `在途 ${taskStats.inFlight}`, value: taskStats.inFlight, color: 'var(--fg-3)' },
                    ].filter((s) => s.value > 0)}
                  />
                </div>

                {/* 失败原因分布 */}
                {taskStats.failureReasons?.length > 0 && (
                  <DataTable>
                    <thead>
                      <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                        <th style={{ padding: '6px 8px' }}>失败原因</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>次数</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskStats.failureReasons.map((f) => (
                        <tr key={f.code} style={{ borderTop: '1px solid var(--bd-1)' }}>
                          <td style={{ padding: '6px 8px' }} className="mono">{f.code}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(f.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                )}
              </>
            ) : (
              <div style={{ padding: '20px 4px', textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
                该 endpoint 窗口内没有异步任务
              </div>
            )}
          </Section>
        )}

        {/* AI 用量面板 —— isAi（数据源 ProviderEndpointAiStatsDaily，endpoint 维度） */}
        {endpointDetail.capabilities?.isAi && (
          <Section
            title="AI 用量"
            action={
              <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                来自 ProviderEndpointAiStatsDaily · 本 endpoint
              </span>
            }
          >
            {aiResource.loading ? (
              <LoadingHint />
            ) : aiUsage?.isAi && aiUsage.totalCalls > 0 ? (
              <>
                <KpiGrid>
                  <KpiCard label="输入 tokens" value={fmtNum(aiUsage.inputTokens)} dim={aiUsage.inputTokens === 0} />
                  <KpiCard label="输出 tokens" value={fmtNum(aiUsage.outputTokens)} dim={aiUsage.outputTokens === 0} />
                  <KpiCard label="cache 读" value={fmtNum(aiUsage.cacheReadTokens)} dim={aiUsage.cacheReadTokens === 0} hint="Anthropic cache_read / OpenAI cached" />
                  <KpiCard
                    label="cache 命中率"
                    value={aiUsage.cacheHitRate > 0 ? `${(aiUsage.cacheHitRate * 100).toFixed(1)}%` : '—'}
                    dim={aiUsage.cacheHitRate === 0}
                    hint="cacheRead / (input + cacheRead)"
                  />
                  <KpiCard
                    label="平均 tok/s"
                    value={aiUsage.avgTokensPerSecond != null ? `${aiUsage.avgTokensPerSecond}` : '—'}
                    dim={!aiUsage.avgTokensPerSecond}
                    hint="仅流式"
                  />
                </KpiGrid>

                {aiUsage.byModel?.length > 0 && (
                  <DataTable>
                    <thead>
                      <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                        <th style={{ padding: '6px 8px' }}>Model</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>输入 tok</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>输出 tok</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>cache 读 tok</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>毛收入</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiUsage.byModel.map((m) => (
                        <tr key={m.modelName} style={{ borderTop: '1px solid var(--bd-1)' }}>
                          <td style={{ padding: '6px 8px' }} className="mono">{m.modelName}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(m.calls)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(m.inputTokens)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(m.outputTokens)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--fg-3)' }} className="mono">{fmtNum(m.cacheReadTokens)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(m.cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                )}
              </>
            ) : (
              <div style={{ padding: '20px 4px', textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
                该 endpoint 窗口内没有 AI 调用
              </div>
            )}
          </Section>
        )}

        {/* WS 会话面板 —— protocol === 'WEBSOCKET'（数据源 ProviderWsSessionStatsDaily，
            独立于 REST 聚合：REST 侧已用 method!='WEBSOCKET' 剔除 WS）。 */}
        {endpointDetail.capabilities?.isWebSocket && (
          <Section
            title="WS 会话"
            action={
              <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                {wsSessions?.adapter ? `${wsSessions.adapter} · ` : ''}来自 ProviderWsSessionStatsDaily
              </span>
            }
          >
            {wsResource.loading ? (
              <LoadingHint />
            ) : wsSessions && wsSessions.totalSessions > 0 ? (
              <>
                <KpiGrid>
                  <KpiCard label="总会话" value={fmtNum(wsSessions.totalSessions)} />
                  <KpiCard
                    label="成功率"
                    value={wsSessions.successRate != null ? fmtPct(wsSessions.successRate / 100) : '—'}
                    dim={wsSessions.successRate == null}
                    hint="1 − 上游失败 / 总会话（不计崩溃孤儿）"
                  />
                  <KpiCard
                    label="上游失败"
                    value={fmtNum(wsSessions.serverErrorSessions)}
                    dim={wsSessions.serverErrorSessions === 0}
                    hint="closeCode 1011 或 upstream 断连"
                  />
                  <KpiCard
                    label="崩溃孤儿"
                    value={fmtNum(wsSessions.orphanedSessions)}
                    dim={wsSessions.orphanedSessions === 0}
                    hint="gateway 崩溃被 reconciler 收口"
                  />
                  <KpiCard
                    label="平均计费时长"
                    value={fmtMs(wsSessions.avgDurationMs)}
                    dim={wsSessions.avgDurationMs == null}
                    hint="上游产出时长（计费口径）"
                  />
                  <KpiCard
                    label="平均会话时长"
                    value={fmtMs(wsSessions.avgWallMs)}
                    dim={wsSessions.avgWallMs == null}
                    hint="墙钟 connect → close"
                  />
                  <KpiCard
                    label="峰值日独立调用方"
                    value={fmtNum(wsSessions.peakDailyUniqueCallers)}
                    dim={wsSessions.peakDailyUniqueCallers === 0}
                    hint="窗口内单日 distinct caller 峰值"
                  />
                  <KpiCard
                    label="毛收入"
                    value={fmtTinyUsd(wsSessions.totalCost)}
                    dim={wsSessions.totalCost === 0}
                  />
                </KpiGrid>

                {/* 会话结果构成 */}
                <div style={{ marginTop: 10 }}>
                  <HorizontalStack
                    height={22}
                    segments={[
                      {
                        label: `正常 ${wsSessions.totalSessions - wsSessions.serverErrorSessions - wsSessions.orphanedSessions}`,
                        value: Math.max(
                          0,
                          wsSessions.totalSessions - wsSessions.serverErrorSessions - wsSessions.orphanedSessions,
                        ),
                        color: '#22a06b',
                      },
                      { label: `上游失败 ${wsSessions.serverErrorSessions}`, value: wsSessions.serverErrorSessions, color: '#e5484d' },
                      { label: `崩溃孤儿 ${wsSessions.orphanedSessions}`, value: wsSessions.orphanedSessions, color: '#e08e0b' },
                    ].filter((s) => s.value > 0)}
                  />
                </div>

                {/* 吞吐 */}
                <div style={{ marginTop: 12 }}>
                  <KpiGrid>
                    <KpiCard label="入向字节" value={fmtBytes(wsSessions.totalBytesIn)} dim={wsSessions.totalBytesIn === 0} />
                    <KpiCard label="出向字节" value={fmtBytes(wsSessions.totalBytesOut)} dim={wsSessions.totalBytesOut === 0} />
                    <KpiCard label="入向帧" value={fmtNum(wsSessions.totalFramesIn)} dim={wsSessions.totalFramesIn === 0} />
                    <KpiCard label="出向帧" value={fmtNum(wsSessions.totalFramesOut)} dim={wsSessions.totalFramesOut === 0} />
                  </KpiGrid>
                </div>

                {/* 会话数日趋势 */}
                {wsSessions.points?.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <BarChart
                      data={wsSessions.points}
                      valueKey="sessions"
                      height={140}
                      emptyZeroHint="该时间窗口内没有 WS 会话"
                    />
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: '20px 4px', textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
                该 endpoint 窗口内没有 WS 会话
              </div>
            )}
          </Section>
        )}

        {/* 调用量 / 延迟 / 成功失败 三张时序图共享同一 timeseries +
            granularity；宽屏并排 3 列。granularity 切换只放在第一张上。 */}
        <ChartGrid minWidth={360}>
          <Section
            compact
            title="调用量时序"
            action={
              <div style={{ display: 'inline-flex', gap: 4 }}>
                {['hour', 'day'].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGranularity(g)}
                    style={{
                      padding: '3px 8px',
                      border: '1px solid var(--bd-1)',
                      background: granularity === g ? 'var(--bg-3)' : 'transparent',
                      color: granularity === g ? 'var(--fg-0)' : 'var(--fg-3)',
                      borderRadius: 4,
                      fontSize: 10,
                      cursor: 'pointer',
                    }}
                  >{g === 'hour' ? '小时' : '日'}</button>
                ))}
              </div>
            }
          >
            {tsResource.loading ? (
              <LoadingHint />
            ) : timeseries?.points?.length ? (
              <BarChart
                data={timeseries.points}
                valueKey="calls"
                height={160}
                emptyZeroHint="该时间窗口内此 endpoint 没有调用"
              />
            ) : (
              <EmptyBarChart height={160} hint="首次调用后这里会显示真实数据" />
            )}
          </Section>

          {/* 延迟时序：endpoint scope 下 P50/P95 全 null，LineChart 自动只画 mean */}
          <Section
            compact
            title="延迟时序"
            action={
              <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                仅 mean（无预聚合 P50/P95）
              </span>
            }
          >
            {tsResource.loading ? (
              <LoadingHint />
            ) : timeseries?.points?.length ? (
              <LineChart
                data={timeseries.points}
                series={[
                  { key: 'avgLatency', label: 'mean', color: 'var(--fg-1)' },
                ]}
                height={160}
                unit="ms"
              />
            ) : (
              <EmptyBarChart height={160} hint="首次调用后这里会显示均值延迟" />
            )}
          </Section>

          {/* 成功 / 失败堆叠柱 */}
          <Section
            compact
            title="成功 / 失败时序"
            action={<span style={{ fontSize: 10, color: 'var(--fg-3)' }}>红 = 4xx + 5xx</span>}
          >
            {tsResource.loading ? (
              <LoadingHint />
            ) : timeseries?.points?.length ? (
              <StackedBarChart data={timeseries.points} height={160} />
            ) : (
              <EmptyBarChart height={160} hint="首次调用后这里会显示成功 / 失败堆叠" />
            )}
          </Section>
        </ChartGrid>

        {/* 延迟构成 */}
        <Section
          title="延迟构成"
        >
          {latencyResource.loading ? (
            <LoadingHint />
          ) : latencyBreakdown?.phases?.some(p => p.samples > 0 || p.avgMs != null) ? (
            <>
              <p style={{ fontSize: 11, color: 'var(--fg-3)' }}>上游耗时包含连接；各阶段样本不同，均值不可相加。</p>
              <DataTable>
                <caption style={{ textAlign: 'left', fontSize: 11, color: 'var(--fg-3)', paddingBottom: 8 }}>延迟阶段样本均值</caption>
                <thead><tr><th>阶段</th><th>平均耗时</th><th>样本数</th></tr></thead>
                <tbody>{latencyBreakdown.phases.map(p => (
                  <tr key={p.key}>
                    <td>{p.label}</td>
                    <td className="mono">{fmtMs(p.avgMs)}</td>
                    <td className="mono">{p.samples == null ? '—' : fmtNum(p.samples)}</td>
                  </tr>
                ))}</tbody>
              </DataTable>
            </>
          ) : (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
              暂无可用 timing 数据
            </div>
          )}
        </Section>

        {/* Top callers 用这个 endpoint */}
        <Section
          title={`Top callers (${callerRevenue?.items?.length || 0})`}
          action={
            callerRevenue?.items?.length > 0 ? (
              <ExportCsvButton
                rows={callerRevenue.items}
                filename={`endpoint-${ep.name}-callers-${new Date().toISOString().slice(0, 10)}.csv`}
                columns={[
                  { key: 'callerRef', label: 'Caller reference' },
                  { key: 'callerHandle', label: 'Caller' },
                  { key: 'calls', label: 'Calls' },
                  { key: 'grossAmount', label: 'Gross USD' },
                  { key: 'firstSeen', label: 'First seen', transform: (v) => new Date(v).toISOString() },
                  { key: 'lastSeen', label: 'Last seen', transform: (v) => new Date(v).toISOString() },
                ]}
              />
            ) : null
          }
        >
          {revenueResource.loading ? (
            <LoadingHint />
          ) : callerRevenue?.items?.length > 0 ? (
            <DataTable>
              <thead>
                <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                  <th style={{ padding: '6px 8px' }}>调用方</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>毛收入</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>占比</th>
                  <th style={{ padding: '6px 8px' }}>最近</th>
                </tr>
              </thead>
              <tbody>
                {callerRevenue.items.slice(0, 10).map((c) => {
                  const pct = callerRevenue.totalGross > 0
                    ? (c.grossAmount / callerRevenue.totalGross) * 100
                    : 0;
                  return (
                    <tr
                      key={c.callerRef}
                      style={{ borderTop: '1px solid var(--bd-1)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '6px 8px' }} className="mono" title={c.callerHandle}>
                        {c.callerHandle}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(c.calls)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(c.grossAmount)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{pct.toFixed(1)}%</td>
                      <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }}>
                        {new Date(c.lastSeen).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </DataTable>
          ) : (
            <EmptyTable cols={4} rows={3} hint="首次调用后这里会列出该 endpoint 的调用方" />
          )}
        </Section>

        {/* 错误分组 */}
        {errorGroups && errorGroups.totalErrors > 0 && (
          <Section
            title={`错误分组 (${fmtNum(errorGroups.totalErrors)} 次失败 · top ${errorGroups.groups.length})`}
          >
            <DataTable>
              <thead>
                <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                  <th style={{ padding: '6px 8px', width: 60 }}>状态</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>次数</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>占比</th>
                  <th style={{ padding: '6px 8px' }}>最近一次</th>
                </tr>
              </thead>
              <tbody>
                {errorGroups.groups.map((g, i) => {
                  const pct = (g.count / errorGroups.totalErrors) * 100;
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--bd-1)' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <StatusCodePill code={g.statusCode} />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(g.count)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{pct.toFixed(1)}%</td>
                      <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }} className="mono">
                        {new Date(g.lastSeen).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </DataTable>
          </Section>
        )}

        {/* 调用日志 */}
        </>}
        {logsError && <div role="alert">日志加载失败 <button onClick={() => fetchLogs({ append: logs.length > 0, cursor: logCursor })}>重试日志</button></div>}
        <Section
          title="调用日志"
          action={
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                自动 scoped 到本 endpoint
              </span>
              <ExportCsvButton
                rows={logs}
                filename={`endpoint-${ep.name}-logs-${new Date().toISOString().slice(0, 10)}.csv`}
                columns={[
                  { key: 'createdAt', label: 'Time', transform: (v) => new Date(v).toISOString() },
                  { key: 'method', label: 'Method' },
                  { key: 'statusCode', label: 'Status' },
                  { key: 'callerHandle', label: 'Caller' },
                  { key: 'responseTime', label: 'Latency ms' },
                  { key: 'cost', label: 'Cost USD' },
                  { key: 'country', label: 'Country' },
                  { key: 'id', label: 'Log ID' },
                ]}
              />
            </div>
          }
        >
          {logsLoading && logs.length === 0 ? (
            <LoadingHint />
          ) : logs.length === 0 ? (
            <EmptyTable cols={7} rows={4} hint="本 endpoint 暂无调用记录" />
          ) : (
            <>
              <DataTable>
                <thead>
                  <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                    <th style={{ padding: '6px 8px' }}>时间</th>
                    <th style={{ padding: '6px 8px' }}>方法</th>
                    <th style={{ padding: '6px 8px' }}>状态</th>
                    <th style={{ padding: '6px 8px' }}>调用方</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>延迟</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>花费</th>
                    <th style={{ padding: '6px 8px' }}>地区</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id} data-log-id={log.id}
                      onClick={() => setActiveLog(log)}
                      style={{ borderTop: '1px solid var(--bd-1)', cursor: 'pointer' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '6px 8px', color: 'var(--fg-2)', whiteSpace: 'nowrap' }} className="mono">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 8px' }}><MethodPill method={log.method} /></td>
                      <td style={{ padding: '6px 8px' }}><StatusCodePill code={log.statusCode} /></td>
                      <td style={{ padding: '6px 8px' }} className="mono">
                        {log.callerRef ? (
                          <span title={log.callerHandle} style={{ color: 'var(--fg-2)' }}>
                            {log.callerHandle}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{log.responseTime}ms</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(log.cost)}</td>
                      <td style={{ padding: '6px 8px' }}><CountryCell iso={log.country} /></td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
              <TableFooter
                hasMore={logHasMore}
                loading={logsLoading}
                totalLoaded={logs.length}
                onLoadMore={() => fetchLogs({ append: true, cursor: logCursor })}
              />
            </>
          )}
        </Section>

        {activeLog && (
          <LogDetailDrawer log={activeLog} onClose={() => setActiveLog(null)} ep={ep} />
        )}
      </div>
    </WorkspaceShell>
  );
};

// 简易日志详情抽屉（复用 traffic 同款的）
const LogDetailDrawer = ({ log, onClose, ep }) => {
  const [full, setFull] = useState(null);
  useEffect(() => {
    XApi.providerDashboardService.logDetail(log.id)
      .then((d) => setFull(d || { ...log }))
      .catch(() => setFull({ ...log }));
  }, [log.id]);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 30, display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 92vw)', background: 'var(--bg-0)',
          padding: '20px 24px', overflowY: 'auto', borderLeft: '1px solid var(--bd-1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>调用详情</h3>
          <button
            type="button" onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--fg-2)', cursor: 'pointer', fontSize: 16 }}
          >×</button>
        </div>
        {full == null ? (
          <LoadingHint />
        ) : (
          <div style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.6 }}>
            <Row label="时间" value={new Date(full.createdAt).toLocaleString()} />
            {ep && (
              <Row
                label="端点"
                value={
                  <span>
                    <MethodPill method={ep.method} /> <span style={{ marginLeft: 6 }}>{ep.name}</span>
                    <span className="mono" style={{ color: 'var(--fg-3)', marginLeft: 6 }}>{ep.path}</span>
                  </span>
                }
              />
            )}
            <Row label="URL" value={<span className="mono">{full.requestUrl}</span>} />
            <Row label="状态码" value={<StatusCodePill code={full.statusCode} />} />
            <Row label="延迟" value={`${full.responseTime} ms`} />
            <Row label="花费" value={fmtTinyUsd(full.cost)} />
            <Row label="调用方" value={full.callerHandle ? <span className="mono">{full.callerHandle}</span> : '—'} />
            <Row label="国家" value={<CountryCell iso={full.country} />} />
            {full.requestHeadersTrunc && <Code label="请求头" value={full.requestHeadersTrunc} />}
            {full.requestBodyTrunc && <Code label="请求体" value={full.requestBodyTrunc} />}
            {full.responseHeadersTrunc && <Code label="响应头" value={full.responseHeadersTrunc} />}
            {full.responseBodyTrunc && <Code label="响应体" value={full.responseBodyTrunc} />}
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--bd-1)' }}>
    <div style={{ width: 80, color: 'var(--fg-3)' }}>{label}</div>
    <div style={{ flex: 1, wordBreak: 'break-all' }}>{value}</div>
  </div>
);
const Code = ({ label, value }) => (
  <div style={{ marginTop: 12 }}>
    <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 4 }}>{label}</div>
    <pre style={{
      background: 'var(--bg-1)', padding: 10, borderRadius: 4, fontSize: 11,
      overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      margin: 0, maxHeight: 240,
    }}>{value}</pre>
  </div>
);
